"use client";

import { useCallback, useRef, useState } from "react";

// Ambient declarations for the Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

function getSpeechRecognition(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  return (
    (window as unknown as Record<string, SpeechRecognitionConstructor>)
      .SpeechRecognition ??
    (window as unknown as Record<string, SpeechRecognitionConstructor>)
      .webkitSpeechRecognition ??
    null
  );
}

export function useVoiceInput() {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const callbackRef = useRef<((text: string) => void) | null>(null);

  const isSupported = typeof window !== "undefined" && getSpeechRecognition() !== null;

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setIsListening(false);
    callbackRef.current = null;
  }, []);

  const start = useCallback(
    (onTranscript: (text: string) => void) => {
      const SpeechRecognition = getSpeechRecognition();
      if (!SpeechRecognition) {
        console.warn("Web Speech API is not supported in this browser.");
        return;
      }

      // Stop any existing session
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-US";

      callbackRef.current = onTranscript;
      recognitionRef.current = recognition;

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            const transcript = result[0].transcript.trim();
            if (transcript) {
              callbackRef.current?.(transcript);
            }
          }
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        // "aborted" fires when we call stop() ourselves — not a real error
        if (event.error === "aborted") return;
        if (event.error === "not-allowed") {
          console.warn("Microphone permission denied.");
        } else {
          console.warn("Speech recognition error:", event.error);
        }
        setIsListening(false);
        recognitionRef.current = null;
        callbackRef.current = null;
      };

      recognition.onend = () => {
        setIsListening(false);
        recognitionRef.current = null;
        callbackRef.current = null;
      };

      try {
        recognition.start();
        setIsListening(true);
      } catch {
        console.warn("Failed to start speech recognition.");
        setIsListening(false);
      }
    },
    [],
  );

  const toggle = useCallback(
    (onTranscript: (text: string) => void) => {
      if (isListening) {
        stop();
      } else {
        start(onTranscript);
      }
    },
    [isListening, start, stop],
  );

  return { isListening, isSupported, start, stop, toggle };
}
